<template>
    <div class="progress-container" role="progressbar" :aria-valuemin="0" :aria-valuemax="100" :aria-valuenow="clamped">
        <div class="progress-bar" :style="barStyle">
            <div class="progress-bar-fill" :class="{ 'no-stripe': !striped }" :style="{ width: clamped + '%' }"></div>
        </div>
        <div v-if="showPercent" class="progress-percentage">{{ clamped }}%</div>
    </div>
</template>

<script>
export default {
    name: 'ProgressContainer',
    props: {
        autostart: { type: Boolean, default: true },
        completed: { type: Boolean, default: false },
        showPercent: { type: Boolean, default: true },
        striped: { type: Boolean, default: true },
        height: { type: [Number, String], default: 8 },
        durationToCapSec: { type: Number, default: 35 }
    },
    data() {
        return {
            value: 0,
            timer: null,
            finishing: false
        }
    },
    computed: {
        clamped() {
            const v = Math.round(Number(this.value) || 0)
            return Math.max(0, Math.min(v, 100))
        },
        barStyle() {
            const h = typeof this.height === 'number' ? `${this.height}px` : this.height
            return { height: h }
        }
    },
    watch: {
        completed(done) {
            if (done) this.finishToHundred()
        }
    },
    mounted() {
        this.value = 0
        if (this.autostart) this.startAuto()
        if (this.completed) this.finishToHundred()
    },
    beforeDestroy() {
        this.stopAuto()
    },
    methods: {
        startAuto() {
            this.stopAuto()
            if (this.completed) return

            const cap = 90
            const tickMs = 1000
            const ticksToCap = Math.max(1, Math.round((this.durationToCapSec * 1000) / tickMs))
            const perTick = (cap - this.value) / ticksToCap

            this.timer = setInterval(() => {
                if (this.completed || this.finishing) return
                if (this.value < cap) {
                    const jitter = perTick * (Math.random() * 0.2 + 0.9)
                    this.value = Math.min(cap, this.value + jitter)
                }
            }, tickMs)
        },
        finishToHundred() {
            if (this.finishing) return
            this.finishing = true
            this.stopAuto()

            const step = () => {
                if (this.value >= 100) {
                    this.value = 100
                    this.finishing = false
                    return
                }
                this.value += Math.max(1, (100 - this.value) * 0.3)
                requestAnimationFrame(step)
            }
            requestAnimationFrame(step)
        },
        stopAuto() {
            if (this.timer) {
                clearInterval(this.timer)
                this.timer = null
            }
        }
    }
}
</script>

<style scoped>
.progress-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 200px;
}

.progress-bar {
    width: 80%;
    background-color: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
}

.progress-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, rgba(94, 121, 255,0.7), rgb(94, 121, 255));
    transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.progress-bar-fill::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, .4), transparent);
    animation: shimmer 2s infinite;
}

.progress-percentage {
    font-size: 13px;
    color: #6c757d;
    text-align: right;
    font-weight: 500;
}

.no-stripe::after {
    display: none;
}

@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }

    100% {
        transform: translateX(100%);
    }
}
</style>
